/**
 * Servicios para verificación de carnets por ID.
 *
 * Este módulo contiene servicios para la verificación de carnets mediante QR,
 * incluyendo funciones para verificar tipos de carnet, obtener datos públicos
 * y privados de carnets, y actualizar el estado de ingreso.
 *
 * @author eulicer zapata <eulicer.zapata@iudigital.edu.co>
 * @date [Fecha de creación: 2025-09-15]
 * @updated [Fecha de última modificación: 2025-09-15]
 * @updatedBy eulicer zapata <eulicer.zapata@iudigital.edu.co>
 */

import fetchWrapper from "../../../fetchWrapper";

/**
 * Extrae parámetros de identificación de una URL de código QR.
 * Analiza la URL del QR para extraer el ID y código QR del carnet,
 * asumiendo una estructura de URL específica: /verify/{id}/{qr}/
 * @param {string} qrUrl - La URL completa del código QR.
 * @returns {Object|null} Objeto con {id, qr} si es válido, null si hay error.
 */
const extractParamsFromQR = (qrUrl) => {
  try {
    const url = new URL(qrUrl);
    const pathParts = url.pathname.split("/").filter((part) => part !== "");

    // Asumiendo estructura: /verify/{id}/{qr}/
    if (pathParts.length >= 3 && pathParts[0] === "verify") {
      return {
        id: pathParts[1],
        qr: pathParts[2],
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Verifica el tipo de carnet de forma pública sin autenticación.
 * Consulta el endpoint público para determinar el tipo de carnet y su validez
 * basándose en los parámetros extraídos de la URL del QR.
 * @param {string} qrUrl - La URL completa del código QR.
 * @returns {Object} Objeto con {cardType, isValid, id, qr, message} si es exitoso, {status: false, message} si hay error.
 */
const getCardType = async (qrUrl) => {
  try {
    const params = extractParamsFromQR(qrUrl);
    if (!params) {
      return { status: false, message: "URL del QR inválida" };
    }

    const { id, qr } = params;
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/public/cards/card_type/${id}/${qr}`
    );

    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      return { status: false, message };
    }

    const result = await response.json();
    const { success } = result;

    if (success === true) {
      const { cardType, isValid, message } = result;
      return { cardType, isValid, id, qr, message };
    } else {
      const message = result.message || "Error al verificar el tipo de carnet";
      return { status: false, message };
    }
  } catch (error) {
    const message = error.message || "Error de conexión al verificar el tipo de carnet";
    return { status: false, message };
  }
};

/**
 * Obtiene los datos del carnet de forma pública sin autenticación.
 * Consulta el endpoint público para obtener todos los detalles del carnet
 * incluyendo información del usuario, fechas de vigencia, estado y tipo.
 * @param {string} id - El identificador único del carnet.
 * @param {string} qr - El código QR del carnet.
 * @returns {Object} Objeto con {payload} si es exitoso, {status: false, message} si hay error.
 */
const getPublicCardData = async (id, qr) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/public/cards/verify_card/${id}/${qr}`
    );

    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      return { status: false, message };
    }

    const result = await response.json();
    const { success } = result;

    if (success === true) {
      const { payload } = result;
      return { payload };
    } else {
      const message = result.message || "Error del servidor al realizar la solicitud";
      return { status: false, message };
    }
  } catch (error) {
    const message = error.message || "Error del servidor al realizar la solicitud";
    return { status: false, message };
  }
};

/**
 * Obtiene los datos del carnet mediante autenticación.
 * Consulta el endpoint autenticado para obtener los detalles del carnet
 * utilizando el wrapper de fetch que incluye el token de autenticación.
 * @param {string} id - El identificador único del carnet.
 * @param {string} qr - El código QR del carnet.
 * @returns {Object} Objeto con {payload} si es exitoso, {status: false, message} si hay error.
 */
const getCardPerId = async (id, qr, isAction) => {
  try {
    const response = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/cards/verify_card_private/${id}/${qr}/${isAction}`
    );
    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }
    const result = await response.json();
    const { success } = result;
    if (success === true) {
      const { payload } = result;
      return { payload };
    } else if (success === false) {
      const message = result.message || "Error del servidor al realizar la solicitud";
      const status = false;
      return { status, message };
    }
  } catch (error) {
    const message = error.message || "Error del servidor al realizar la solicitud";
    const status = false;
    return { status, message };
  }
};

/**
 * Actualiza el estado de ingreso de un carnet.
 * Permite marcar un carnet como "ingresado" mediante una petición POST
 * al endpoint de edición de carnets con autenticación requerida.
 * @param {string} id - El identificador único del carnet a actualizar.
 * @param {Object} statusCarnet - Objeto con el estado del carnet {joined: "1"}.
 * @returns {Object} Resultado de la operación si es exitoso, {status: false, message} si hay error.
 */
const updatedJoinCarnet = async (id, statusCarnet) => {
  try {
    const response = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/cards/edit/${id}`,
      JSON.stringify(statusCarnet),
      "POST"
    );
    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }
    const result = await response.json();
    const { success, code } = result;
    if (success === true) {
      return result;
    } else if (success === false) {
      const message = result.message || "Error del servidor al realizar la solicitud";
      const status = false;
      return { status, message };
    }
  } catch (error) {
    const message = error.message || "Error del servidor al realizar la solicitud";
    const status = false;
    return { status, message };
  }
};

export { getCardType, getCardPerId, getPublicCardData, updatedJoinCarnet, extractParamsFromQR };
